import { icon } from '@fortawesome/fontawesome-svg-core';
import { FaIcon, ReadMore } from '@innovationnorway/component-library';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OneWayInOpportunityStatusDto, OneWayInStatus, SivaPhase } from 'src/api/v2';
import Table from 'src/components/Table';
import TertiaryButton from 'src/components/TertiaryButton/TertiaryButton';
import { formatDateToNOString } from 'src/utils/FormatValue';

import './HistoryTableSection.scss';
import { EmailViewContent } from './OneWayInHistoryV2';
import { useUsers } from 'src/features/users';
import { useParams } from 'react-router-dom';
import { PageParams } from '../../types/PageParams';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import { useClusters } from 'src/features/cluster';

export type HistoryTableSectionProps = {
  date: Date;
  data: OneWayInOpportunityStatusDto[];
  openEmailView?: (content: EmailViewContent) => void;
};

const HistoryTableSection: React.FC<HistoryTableSectionProps> = ({ date, data, openEmailView }) => {
  const { t: tCommon } = useTranslation();
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tSiva } = useTranslation('siva');
  const { users } = useUsers();
  const { formId } = useParams<PageParams>();
  const { oneWayInGroupedFormWOEnrollmentsQuery } = useOneWayIn(formId);
  const { clusters } = useClusters();

  function statusToIcon(item: OneWayInOpportunityStatusDto): React.ReactNode {
    switch (item.status) {
      case OneWayInStatus.ReOpened:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'folder-open' })} />;
      case OneWayInStatus.Opened:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'envelope-open' })} />;
      case OneWayInStatus.Closed:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'box-archive' })} />;
      case OneWayInStatus.AutoArchived:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'box-archive' })} />;
      case OneWayInStatus.Forwarded:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'paper-plane' })} />;
      case OneWayInStatus.Shared: {
        if (item.shareOpportunityCustomerEmail)
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'envelope' })} />;
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'paper-plane' })} />;
      }
      case OneWayInStatus.Active: {
        // Tatt opp til idé
        if (item.sivaPhase == SivaPhase.Idea) {
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'lightbulb' })} />;
        }

        // Anbefalt tjeneste
        if (item.recommendedServices.length > 0) {
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'hand-holding-box' })} />;
        }

        // Registrert tjeneste
        if (item.registeredServices.length > 0) {
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'grid-2-plus' })} />;
        }

        // Tatt opp i Siva-program
        if (item.admittedToProgram || isActivePhase(item.sivaPhase)) {
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'handshake' })} />;
        }

        // Notat
        if (item.description !== '') {
          return <FaIcon icon={icon({ prefix: 'fal', iconName: 'sticky-note' })} />;
        }

        return null;
      }
      case OneWayInStatus.Received:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'down-to-bracket' })} />;
      case OneWayInStatus.FollowUpLater:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'clock' })} />;
      case OneWayInStatus.EmailReceivedFromCustomer:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'messages' })} />;
      case OneWayInStatus.EmailSentToCustomer:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'messages' })} />;
      case OneWayInStatus.ReferredLater:
        return <FaIcon icon={icon({ prefix: 'fal', iconName: 'paper-plane' })} />;

      default:
        return null;
    }
  }

  function statusToText(item: OneWayInOpportunityStatusDto): string {
    switch (item.status) {
      case OneWayInStatus.Active: {
        if (item.sivaPhase != null) {
          if (item.sivaPhase == SivaPhase.Idea) {
            return tSiva('ideaConsidered');
          } else {
            return tSiva('admittedToProgram');
          }
        }

        if (item.recommendedServices.length > 0) {
          return tOneWayIn('services.recommended');
        }

        if (item.registeredServices.length > 0) {
          return tOneWayIn('services.delivered');
        }

        if (item.admittedToProgram) {
          return tSiva('admittedToProgram');
        }

        if (item.description !== '') {
          return tCommon('note');
        }

        return tOneWayIn(`status.${item.status}` as const);
      }
      case OneWayInStatus.Forwarded:
      case OneWayInStatus.Shared: {
        if (item.shareOpportunityCustomerEmail) return tCommon('email');
        return tOneWayIn('status.Referred');
      }
      case OneWayInStatus.Closed:
        return tOneWayIn('status.Archived');
      case OneWayInStatus.FollowUpLater:
        return tOneWayIn('status.FollowUpLater');
      case OneWayInStatus.EmailReceivedFromCustomer:
        return tOneWayIn('status.EmailReceivedFromCustomer');
      case OneWayInStatus.EmailSentToCustomer:
        return tOneWayIn('status.EmailSentToCustomer');
      default:
        return tOneWayIn(`status.${item.status}` as const);
    }
  }

  function renderStatusExtraInfo(item: OneWayInOpportunityStatusDto): React.ReactNode {
    switch (item.status) {
      case OneWayInStatus.Shared:
        return item.shareOpportunityCustomerEmail == null ? (
          <small>
            {tCommon('to')} {item.referenceClusterName}
          </small>
        ) : (
          <small>{tOneWayIn('history.sentToCustomer')}</small>
        );
      case OneWayInStatus.Received:
        return (
          <small>
            {tCommon('from')} {item.referenceClusterName}
          </small>
        );
      case OneWayInStatus.Active: {
        if (item.recommendedServices.length > 0) {
          const clusterName = clusters.find(
            (cluster) => cluster.id === item.recommendedServices[0].clusterId,
          )?.name;
          return (
            <>
              <ul className="margin-y--0">
                {item.recommendedServices.map((s) => (
                  <li key={s.id}>
                    <small>{s.name}</small>
                  </li>
                ))}
              </ul>
              <small>{clusterName && `${tCommon('at')} ${clusterName}`}</small>
            </>
          );
        }

        if (item.registeredServices.length > 0) {
          return (
            <ul className="margin-y--0">
              {item.registeredServices.map((s) => (
                <li key={s.id}>
                  <small>{s.name}</small>
                </li>
              ))}
            </ul>
          );
        }

        return null;
      }
      case OneWayInStatus.EmailSentToCustomer:
        return <span>{tOneWayIn('history.sentToCustomerWithoutReply')}</span>;
      case OneWayInStatus.EmailReceivedFromCustomer:
        return <span>{tOneWayIn('history.sentToCustomerWithReply')}</span>;
      case OneWayInStatus.ReferredLater:
        return (
          <small>
            {tCommon('to')} {item.clusterName}
          </small>
        );
      default:
        return null;
    }
  }

  const dateString = useMemo(() => {
    return formatDateToNOString(new Date(date));
  }, [date]);

  const emailContent = (item: OneWayInOpportunityStatusDto, index: number) => {
    if (item.shareOpportunityActorEmail) {
      return {
        title: 'Ny henvisning i Flerbedriftsportalen',
        from: 'noreply@innovasjonnorge.no',
        to: item.shareOpportunityActorEmail?.recipients?.join(', ') || '',
        date: formatDateToNOString(item.createdOn, false, '{date}.{month}.{year}', true),
        text: item.shareOpportunityActorEmail?.emailContent || '',
        recommendedMail: false,
        referralMailActor: true,
        referralMailCustomer: false,
        opportunityTitle: oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel || '',
        receivingActor: item.referenceClusterName,
        fromActorName: item.clusterName,
        handlerName: item.handlersName,
      };
    } else if (item.shareOpportunityCustomerEmail) {
      return {
        title: 'Informasjon om henvisning',
        from: 'noreply@innovasjonnorge.no',
        to: item.shareOpportunityCustomerEmail?.recipients?.join(', ') || '',
        date: formatDateToNOString(item.createdOn, false, '{date}.{month}.{year}', true),
        text: item.shareOpportunityCustomerEmail?.emailContent || '',
        recommendedMail: false,
        referralMailActor: false,
        referralMailCustomer: true,
        handlerName: item.handlersName,
        opportunityTitle: oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel || '',
        // The status entry for customer referral email does not store the receiving actor name, but the status entry for actor referral email does,
        // therefore index - 1 because actor email is the previous status entry
        receivingActor: data[index - 1].referenceClusterName,
        fromActorName: item.clusterName,
      };
    } else {
      return {
        title: `Anbefalte tjenester hos ${item.recommendationEmail?.senderOrganisation}`,
        from: 'noreply@innovasjonnorge.no',
        to: item.recommendationEmail?.recipientName || '',
        date: formatDateToNOString(item.createdOn, false, '{date}.{month}.{year}', true),
        text: item.recommendationEmail?.emailContent || '',
        recommendedMail: true,
        referralMailActor: false,
        referralMailCustomer: false,
        actorName: item.recommendationEmail?.senderOrganisation,
        recommendedServices: item.recommendedServices.map((service) => ({
          id: service.id || '',
          name: service.name,
          owner: service.businessName,
          links: service.externalLinks,
          description: service.description,
        })),
        handlerName: item.recommendationEmail?.senderName,
        handlerEmail: users.find((u) => u.id === item.clusterUserId)?.emailAddress,
        handlerPhone: users.find((u) => u.id === item.clusterUserId)?.phone,
      };
    }
  };

  const isActivePhase = (phase: SivaPhase | undefined) =>
    phase != null && phase != SivaPhase.Idea && phase != SivaPhase.Alumni;

  const sortedData = data.sort((a, b) => (a.createdOn > b.createdOn ? -1 : 1));

  const activePhases = sortedData.filter((value) => isActivePhase(value.sivaPhase));

  return (
    <>
      <Table.Row className="owi-history__table-header--no-bottom-border">
        <Table.Cell colSpan={5}>
          <strong>{dateString}</strong>
        </Table.Cell>
      </Table.Row>

      {sortedData
        .filter((value) => {
          if (activePhases.length > 1 && isActivePhase(value.sivaPhase) && value != activePhases[0])
            return false;
          return true;
        })
        .map((item, index) => {
          return (
            <React.Fragment key={item.id}>
              <Table.Row className="one-way-in-history__row-content">
                <Table.Cell data-level="1">{statusToIcon(item)}</Table.Cell>
                <Table.Cell data-level="1">
                  <div className="display--flex flex-direction--column">
                    <strong>{statusToText(item)}</strong>
                    {renderStatusExtraInfo(item)}
                  </div>
                </Table.Cell>
                <Table.Cell data-level="1">
                  {item.status === OneWayInStatus.ReferredLater
                    ? tCommon('innovationNorway')
                    : item.clusterName}
                </Table.Cell>
                <Table.Cell data-level="1">
                  {(item.status !== OneWayInStatus.Opened && item.handlersName) || ''}
                </Table.Cell>
                <Table.Cell data-level="1">
                  {item.recommendationEmail &&
                  item.recommendationEmail.emailContent !== '' &&
                  item.recommendationEmail.emailContent !== undefined &&
                  (item.shareOpportunityActorEmail ||
                    item.shareOpportunityCustomerEmail ||
                    (item.recommendedServices && item.recommendedServices.length > 0)) ? (
                    <TertiaryButton
                      standardizedSize={'x-large'}
                      icon={{ prefix: 'fal', iconName: 'browsers' }}
                      onClick={() => !!openEmailView && openEmailView(emailContent(item, index))}
                    >
                      {tOneWayIn('history.readEmail')}
                    </TertiaryButton>
                  ) : (
                    <ReadMore
                      text={item.description || ''}
                      ideal={100}
                      min={80}
                      max={2048}
                      readMoreText={tCommon('showMore')}
                      readMoreTextVisible={tCommon('showLess')}
                    />
                  )}
                </Table.Cell>
              </Table.Row>
            </React.Fragment>
          );
        })}
    </>
  );
};

HistoryTableSection.displayName = 'Tr';

export default HistoryTableSection;
